import type { ReactNode } from 'react'
import { Box, ButtonBase, IconButton, Paper, Skeleton, Tooltip, Typography } from '@mui/material'
import InsertChartIcon from '@mui/icons-material/InsertChartOutlined'
import TrendingUpIcon from '@mui/icons-material/TrendingUp'
import TrendingDownIcon from '@mui/icons-material/TrendingDown'
import ArrowForwardIcon from '@mui/icons-material/ArrowForward'
import { palette } from './theme/tokens.js'

export interface KpiCardProps {
  label: string
  value: string
  unit?: string
  trend?: number
  warning?: boolean
  loading?: boolean
  /**
   * Rend la carte CLIQUABLE, vers la liste qui explique le chiffre.
   *
   * ⭐ Un indicateur qui alerte sans mener nulle part oblige à retrouver soi-même
   * les lignes concernées — « 7 produits en alerte », puis à l'utilisateur de
   * deviner lesquels. Quand la carte sait où elle mène, elle le dit et y va.
   */
  onClick?: () => void
  /** Ce que le clic va montrer — annoncé sous le chiffre. */
  actionLabel?: string
  /**
   * Le DÉTAIL du chiffre, sous la valeur.
   *
   * ⭐ Un total de sorties d'argent ne se pilote pas : il faut savoir ce qui
   * vient de la marchandise et ce qui vient des frais fixes. Les deux lignes
   * tiennent sous le chiffre plutôt que dans une page à part — c'est en le
   * lisant qu'on se pose la question.
   */
  detail?: ReactNode
  /**
   * Ouvre le GRAPHE de l'indicateur dans le temps.
   *
   * ⭐ Le chiffre dit où l'on en est ; le graphe dit si c'est bien. « 575 000 »
   * aujourd'hui ne signifie rien tant qu'on ne le voit pas à côté des
   * vingt-neuf jours d'avant.
   */
  onGraphe?: () => void
}

/** Carte d'indicateur du tableau de bord (W-07). */
export function KpiCard({
  label,
  value,
  unit,
  trend,
  warning,
  loading,
  onClick,
  actionLabel,
  detail,
  onGraphe,
}: KpiCardProps): JSX.Element {
  const cliquable = Boolean(onClick) && !loading
  return (
    <Paper
      sx={{
        p: 5,
        borderRadius: 3,
        minWidth: 0,
        position: 'relative',
        ...(cliquable
          ? {
              cursor: 'pointer',
              transition: 'none',
              '&:hover': { borderColor: 'primary.main', bgcolor: 'action.hover' },
            }
          : {}),
      }}
    >
      {/*
        ⚠️ Le clic de la carte est un VRAI bouton, étendu sur toute sa surface,
        et non plus la carte elle-même déguisée en bouton. Depuis que la carte
        porte un second geste — le graphe —, un bouton logé DANS un bouton
        serait invalide : un lecteur d'écran n'en annoncerait qu'un, et le clic
        sur l'icône déclencherait aussi la navigation. Deux boutons frères,
        chacun à sa place.
      */}
      {cliquable ? (
        <ButtonBase
          disableRipple
          onClick={onClick}
          aria-label={`${label} : ${value}${actionLabel ? ` — ${actionLabel}` : ''}`}
          sx={{
            position: 'absolute',
            inset: 0,
            zIndex: 1,
            borderRadius: 3,
            '&.Mui-focusVisible': { outline: 2, outlineColor: 'primary.main', outlineStyle: 'solid' },
          }}
        />
      ) : null}

      {onGraphe && !loading ? (
        <Tooltip title="Voir le graphe">
          <IconButton
            size="small"
            onClick={onGraphe}
            aria-label={`Voir le graphe : ${label}`}
            sx={{ position: 'absolute', top: 12, right: 12, zIndex: 2, color: 'text.secondary' }}
          >
            <InsertChartIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      ) : null}

      <Typography
        variant="caption"
        sx={{
          color: 'text.secondary',
          textTransform: 'uppercase',
          letterSpacing: '0.04em',
          display: 'block',
          pr: onGraphe ? 6 : 0,
        }}
      >
        {label}
      </Typography>

      {loading ? (
        // Squelette plutôt qu'une roue : la mise en page ne saute pas au chargement.
        <Skeleton variant="text" width="70%" height={38} sx={{ mt: 1 }} />
      ) : (
        <Typography
          className="tabular"
          sx={{ fontSize: 28, lineHeight: '34px', fontWeight: 700, mt: 1 }}
        >
          {value}
          {unit ? (
            <Typography component="span" sx={{ fontSize: 14, ml: 1, color: 'text.secondary' }}>
              {unit}
            </Typography>
          ) : null}
        </Typography>
      )}

      {detail && !loading ? <Box sx={{ mt: 2 }}>{detail}</Box> : null}

      {trend !== undefined && !loading ? (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1 }}>
          {trend >= 0 ? (
            <TrendingUpIcon sx={{ fontSize: 16, color: palette.success[500] }} />
          ) : (
            <TrendingDownIcon sx={{ fontSize: 16, color: palette.danger[500] }} />
          )}
          <Typography
            variant="caption"
            sx={{ color: trend >= 0 ? palette.success[700] : palette.danger[700] }}
          >
            {trend >= 0 ? '+' : ''}
            {trend} %
          </Typography>
        </Box>
      ) : null}

      {warning && !loading ? (
        <Typography variant="caption" sx={{ color: palette.warning[700], mt: 1, display: 'block' }}>
          Attention requise
        </Typography>
      ) : null}

      {cliquable && actionLabel ? (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 2, color: 'primary.main' }}>
          <Typography variant="caption" sx={{ fontWeight: 600 }}>
            {actionLabel}
          </Typography>
          <ArrowForwardIcon sx={{ fontSize: 14 }} />
        </Box>
      ) : null}
    </Paper>
  )
}
